import { ErrorToaster } from "./toasterHelper";
import { ERROR_MESSAGES } from "../utils/constants";
import { api } from "../services/api";
import { AxiosRequestConfig, AxiosError } from "axios";

// Types
interface ApiResponse<T = any> {
  data?: T;
  message?: string;
  status?: string;
  statusCode?: number;
}

interface ApiError {
  code: number;
  response: ApiResponse;
  message?: string;
}

// const axiosDefaultConfig: AxiosRequestConfig = {
//   withCredentials: true,
// }; To be added back before the final deployment

const isRequestTimeoutError = (error: AxiosError): boolean => {
  if (error.code === "ECONNABORTED") {
    return true;
  }
  const message = (error.message ?? "").toLowerCase();
  return message.includes("timeout") && message.includes("exceeded");
};

const isGenericApiMessage = (message: string | undefined): boolean => {
  if (!message) {
    return true;
  }
  const normalized = message.toLowerCase();
  return (
    normalized.includes("something went wrong") ||
    message === ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED
  );
};

const mapKnownApiDataMessage = (dataMessage: string): string | undefined => {
  if (dataMessage.toLowerCase().includes("maximum upload size")) {
    return ERROR_MESSAGES.IMAGE_FILE_TOO_LARGE;
  }
  return undefined;
};

export const resolveEnvelopeErrorMessage = (
  responseData: ApiResponse | undefined,
  fallback: string = ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED
): string => {
  const message = responseData?.message;
  const dataMessage =
    typeof responseData?.data === "string" ? responseData.data : "";

  if (dataMessage) {
    const mappedMessage = mapKnownApiDataMessage(dataMessage);
    if (mappedMessage) {
      return mappedMessage;
    }
    if (isGenericApiMessage(message)) {
      return dataMessage;
    }
  }

  return message || dataMessage || fallback;
};

export const extractApiErrorMessage = (
  error: unknown,
  fallback: string = ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED
): string => {
  const axiosError = error as AxiosError<ApiResponse>;
  if (isRequestTimeoutError(axiosError)) {
    return ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED;
  }

  const responseData = axiosError?.response?.data;
  if (responseData && typeof responseData === "object" && !Array.isArray(responseData)) {
    const envelopeMessage = resolveEnvelopeErrorMessage(responseData, fallback);
    if (envelopeMessage !== fallback || responseData.message || responseData.data) {
      return envelopeMessage;
    }
  }

  return axiosError?.message || fallback;
};

const handleApiError = (error: any, showError: boolean = false): never => {
  const axiosError = error as AxiosError<ApiResponse>;
  const errorMessage = extractApiErrorMessage(
    axiosError,
    ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED
  );
  const errorCode =
    typeof axiosError?.response?.status === "number"
      ? axiosError.response.status
      : undefined;
  if (showError) {
    ErrorToaster(errorMessage, errorCode);
  }
  throw error;
};

const throwIfEnvelopeError = (responseData: ApiResponse | undefined): void => {
  if (
    !responseData ||
    typeof responseData !== "object" ||
    Array.isArray(responseData)
  ) {
    return;
  }
  if (responseData.status !== "ERROR") {
    return;
  }
  const message = resolveEnvelopeErrorMessage(responseData);
  const statusCode =
    typeof responseData.statusCode === "number"
      ? responseData.statusCode
      : 400;
  const error = new Error(message) as any;
  error.response = { data: responseData, status: statusCode };
  throw error;
};

export const apiClient = {
  async get<T = any>(
    url: string,
    signal?: AbortSignal,
    showError?: boolean,
    useAccessToken: boolean = false
  ): Promise<T> {
    try {
      const config: AxiosRequestConfig = {
        // ...axiosDefaultConfig,
        signal,
        useAccessToken: useAccessToken,
      };
      const response = await api.get<ApiResponse<T>>(url, config);
      throwIfEnvelopeError(response.data as ApiResponse);
      return (response.data.data ?? response.data) as T;
    } catch (error: any) {
      if (error.name === "AbortError" || error.name === "CanceledError") {
        throw error;
      }
      return handleApiError(error, showError || false);
    }
  },

  async post<T = any>(
    url: string,
    data?: any,
    headers?: Record<string, string>,
    signal?: AbortSignal,
    showError?: boolean,
    useAccessToken: boolean = false,
    skipAuth: boolean = false
  ): Promise<T> {
    try {
      const config: AxiosRequestConfig = {
        // ...axiosDefaultConfig,
        headers: headers || {},
        signal,
        useAccessToken: useAccessToken,
        skipAuth,
      };
      if (data instanceof FormData && config.headers) {
        delete config.headers["Content-Type"];
      }
      const response = await api.post<ApiResponse<T>>(url, data, config);
      throwIfEnvelopeError(response.data as ApiResponse);
      return (response.data.data ?? response.data) as T;
    } catch (error: any) {
      if (error.name === "AbortError" || error.name === "CanceledError") {
        throw error;
      }
      return handleApiError(error, showError || false);
    }
  },

  async put<T = any>(
    url: string,
    data?: any,
    headers?: Record<string, string>,
    showError?: boolean,
    useAccessToken: boolean = false
  ): Promise<T> {
    try {
      const config: AxiosRequestConfig = {
        // ...axiosDefaultConfig,
        useAccessToken: useAccessToken,
      };
      if (headers) {
        config.headers = headers;
        if (data instanceof FormData) {
          delete config.headers["Content-Type"];
        }
      }
      const response = await api.put<ApiResponse<T>>(url, data, config);
      throwIfEnvelopeError(response.data as ApiResponse);
      return (response.data.data ?? response.data) as T;
    } catch (error: any) {
      if (error.name === "AbortError" || error.name === "CanceledError") {
        throw error;
      }
      return handleApiError(error, showError || false);
    }
  },

  async remove<T = any>(
    url: string,
    data?: any,
    showError?: boolean,
    useAccessToken: boolean = false
  ): Promise<T> {
    try {
      const config: AxiosRequestConfig = {
        // ...axiosDefaultConfig,
        data,
        useAccessToken: useAccessToken,
      };
      const response = await api.delete<ApiResponse<T>>(url, config);
      throwIfEnvelopeError(response.data as ApiResponse);
      return (response.data.data ?? response.data) as T;
    } catch (error: any) {
      if (error.name === "AbortError" || error.name === "CanceledError") {
        throw error;
      }
      return handleApiError(error, showError || false);
    }
  },
};

export const { get, post, put, remove } = apiClient;

export type { ApiResponse, ApiError };
